import { useState } from "react";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";

import { MAX_TFC_NOTES_LENGTH } from "constants/index";

import { TfcAmendButton } from "./TfcAmendButton";
import { TfcEditableTable } from "./TfcEditableTable";
import { TfcResubmitButton } from "./TfcResubmitButton";

import type { EditableLineItem } from "./TfcEditableTable";

type RevisionOperation = Extract<TfcOperation, "AMEND" | "RESUBMIT">;

interface TfcRevisionFormProps {
  tfcId: number;
  operation: RevisionOperation;
  initialLineItems: EditableLineItem[];
  initialNote: string;
  bookedPriceCents: number;
  currency: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function areLineItemsEqual(
  a: EditableLineItem[],
  b: EditableLineItem[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (item, idx) =>
      item.description === b[idx].description &&
      item.line_total_cents === b[idx].line_total_cents,
  );
}

export function TfcRevisionForm(props: TfcRevisionFormProps) {
  const {
    tfcId,
    operation,
    initialLineItems,
    initialNote,
    bookedPriceCents,
    currency,
    onCancel,
    onSuccess,
  } = props;

  const [lineItems, setLineItems] =
    useState<EditableLineItem[]>(initialLineItems);
  const [note, setNote] = useState(initialNote);

  const hasIncompleteItem = lineItems.some(
    (item) => !item.description.trim() || item.line_total_cents === 0,
  );
  // Gates the submit button so carriers can't resubmit an identical revision — per spec,
  // submit only enables once something (line items or note) has actually been changed.
  const isDirty =
    !areLineItemsEqual(lineItems, initialLineItems) || note !== initialNote;
  const submitDisabled = hasIncompleteItem || !isDirty;

  return (
    <Box>
      <TfcEditableTable
        bookedPriceCents={bookedPriceCents}
        currency={currency}
        lineItems={lineItems}
        onLineItemsChange={setLineItems}
      />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 500, mb: 1 }}>
          Notes (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Add any context for these charges..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: MAX_TFC_NOTES_LENGTH }}
          helperText={`${note.length}/${MAX_TFC_NOTES_LENGTH} characters`}
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={onCancel} sx={{ px: 3 }}>
          BACK
        </Button>
        {operation === "AMEND" ? (
          <TfcAmendButton
            tfcId={tfcId}
            lineItems={lineItems}
            note={note}
            bookedPriceCents={bookedPriceCents}
            currency={currency}
            disabled={submitDisabled}
            onSuccess={onSuccess}
          />
        ) : (
          <TfcResubmitButton
            tfcId={tfcId}
            lineItems={lineItems}
            note={note}
            bookedPriceCents={bookedPriceCents}
            currency={currency}
            disabled={submitDisabled}
            onSuccess={onSuccess}
          />
        )}
      </Stack>
    </Box>
  );
}
